import { CloseTaskPayload } from "../api/useCloseTask";
import { MoveUsersToTeamPayload } from "../api/useMoveUsersToTeam";
import { ResignTasksPayload } from "../api/useReassignTasks";

export enum TEAM_DELETION_FLOW_OPERATIONS {
  MOVE_USERS_TO_TEAM = "MOVE_USERS_TO_TEAM",
  REASSIGN_TASKS = "REASSIGN_TASKS",
  CLOSE_TASKS = "CLOSE_TASKS",
}

// #region types
type MoveUsersToTeamOperation = {
  type: TEAM_DELETION_FLOW_OPERATIONS.MOVE_USERS_TO_TEAM;
  payload: MoveUsersToTeamPayload;
};
type CloseTaskOperation = {
  type: TEAM_DELETION_FLOW_OPERATIONS.CLOSE_TASKS;
  payload: CloseTaskPayload;
};
type ReassignTasksOperation = {
  type: TEAM_DELETION_FLOW_OPERATIONS.REASSIGN_TASKS;
  payload: ResignTasksPayload;
};
type TeamDeletionOperation =
  | CloseTaskOperation
  | ReassignTasksOperation
  | MoveUsersToTeamOperation;
// #endregion

// #region utils
export const isMoveUsersToTeamOperation = (
  operation: TeamDeletionOperation
): operation is MoveUsersToTeamOperation =>
  operation.type === TEAM_DELETION_FLOW_OPERATIONS.MOVE_USERS_TO_TEAM;

export const isCloseTaskOperation = (
  operation: TeamDeletionOperation
): operation is CloseTaskOperation =>
  operation.type === TEAM_DELETION_FLOW_OPERATIONS.CLOSE_TASKS;

export const isReassignTasksOperation = (
  operation: TeamDeletionOperation
): operation is ReassignTasksOperation =>
  operation.type === TEAM_DELETION_FLOW_OPERATIONS.REASSIGN_TASKS;
// #endregion

/**
 * Keeps the wizard's pending operations in one stable, imperative registry.
 * This is intentionally not React state: the list is only read when the flow
 * completes, so state would cause unnecessary renders and would not fit the
 * register/unregister/clear mutation API used by the individual steps.
 *
 * One type of operation is allowed per operation type, so registering a new operation
 * of the same type will overwrite the previous one.
 *
 * Operations are started (not awaited sequentially) in the order they were
 * registered and run concurrently, so steps that register operations should
 * not rely on one operation's side effects being visible to another.
 */
export class TeamDeletionFlowOperations {
  private readonly operations = new Map<
    TEAM_DELETION_FLOW_OPERATIONS,
    TeamDeletionOperation
  >();

  register = (operation: TeamDeletionOperation): void => {
    this.operations.set(operation.type, operation);
  };

  unregister = (operation: TeamDeletionOperation): void => {
    this.operations.delete(operation.type);
  };

  clear = (): void => {
    this.operations.clear();
  };

  getOperations = (): TeamDeletionOperation[] => {
    return [...this.operations.values()];
  };
}
